import { useEffect, useRef, useState } from 'react';
import { LanguageSplash } from '@shared/ui';
import {
  getSelectedLanguage,
  setSelectedLanguage,
  supportedLanguages,
} from '@shared/config/translations';
import './LanguageSwitcher.css';

const LANGUAGE_LABELS = {
  en: { short: 'EN', full: 'English' },
  es: { short: 'ES', full: 'Español' },
};

const ChevronIcon = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/**
 * LanguageSwitcher — pill button + popover, anchored top-right of the
 * AuthShell. Keeps the public pages bilingual without a full menu.
 *
 * Picking a different language dispatches `changeLanguage` and reloads
 * the page (matches the legacy LanguageSelector — i18next resources
 * load on init only).
 */
const LanguageSwitcher = () => {
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const wrapRef = useRef(null);
  const current = getSelectedLanguage();
  const currentLabel = LANGUAGE_LABELS[current] || LANGUAGE_LABELS.en;

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = lang => {
    if (lang === current) {
      setOpen(false);
      return;
    }
    setOpen(false);
    // Show the splash overlay first so the upcoming hard reload feels
    // intentional rather than a flash. We deliberately skip the
    // `changeLanguage` dispatch — that would re-render every component
    // with the new strings BEFORE the reload kicks in, producing the
    // "letters swap, then page blanks" stutter the user reported.
    // Persisting to localStorage is enough; on reload, translations.js
    // reads it and the i18n slice initialises from the new language.
    setReloading(true);
    // Two animation frames so the overlay is committed to the screen
    // before we fire the synchronous reload (some browsers paint
    // blank during reload — the overlay covers that gap too).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSelectedLanguage(lang);
        window.location.reload();
      });
    });
  };

  return (
    <>
      {reloading && <LanguageSplash />}
      <div className="qhs-lang-switcher" ref={wrapRef}>
      <button
        type="button"
        className="qhs-lang-switcher__pill"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Language: ${currentLabel.full}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className="qhs-lang-switcher__short">{currentLabel.short}</span>
        <ChevronIcon />
      </button>
      {open && (
        <ul className="qhs-lang-switcher__menu" role="listbox" aria-label="Languages">
          {supportedLanguages.map(lang => {
            const label = LANGUAGE_LABELS[lang];
            if (!label) return null;
            const isCurrent = lang === current;
            return (
              <li key={lang} role="option" aria-selected={isCurrent}>
                <button
                  type="button"
                  className={`qhs-lang-switcher__item${isCurrent ? ' qhs-lang-switcher__item--active' : ''}`}
                  onClick={() => pick(lang)}
                >
                  <span className="qhs-lang-switcher__item-short">{label.short}</span>
                  <span className="qhs-lang-switcher__item-full">{label.full}</span>
                  <span className="qhs-lang-switcher__item-check">{isCurrent && <CheckIcon />}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      </div>
    </>
  );
};

export default LanguageSwitcher;
